import { Link, useLocation } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import { ArrowLeft } from "lucide-react"
import { LandingFooter } from "@/components/landing/LandingFooter"
import { MarketingBrand } from "@/components/landing/MarketingBrand"
import { Seo } from "@/components/seo/Seo"
import { SITE } from "@/lib/site"
import privacyContent from "@/content/privacy-policy.md?raw"
import termsContent from "@/content/terms-of-service.md?raw"
import cookieContent from "@/content/cookie-policy.md?raw"
import securityContent from "@/content/security.md?raw"

const PAGES: Record<string, { content: string; title: string; description: string }> = {
  "/privacy": { content: privacyContent, title: "Privacy Policy", description: "How BidContext collects, uses, and protects your data." },
  "/terms": { content: termsContent, title: "Terms of Service", description: "The terms governing your use of BidContext." },
  "/cookies": { content: cookieContent, title: "Cookies Policy", description: "How BidContext uses cookies and similar technologies." },
  "/security": { content: securityContent, title: "Security", description: "How BidContext keeps your tenders, library, and drafts secure." },
}

export function LegalPage() {
  const { pathname } = useLocation()
  const page = PAGES[pathname]

  if (!page) return null

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Seo
        title={`${page.title} - BidContext`}
        description={page.description}
        canonical={pathname}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE.baseUrl}/` },
            { '@type': 'ListItem', position: 2, name: page.title, item: `${SITE.baseUrl}${pathname}` },
          ],
        }}
      />
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#e5e7eb]/40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <MarketingBrand />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-[#4a4a4a] hover:text-[#6024EE] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">
        <article className="prose prose-neutral max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-lg prose-h3:text-base prose-p:text-sm prose-li:text-sm prose-a:text-[#6024EE] prose-a:no-underline hover:prose-a:underline prose-strong:font-semibold">
          <ReactMarkdown>{page.content}</ReactMarkdown>
        </article>
      </main>

      {/* Footer */}
      <LandingFooter variant="minimal" />
    </div>
  )
}
